import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../utils/api";
import "./Login.css";

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Disable button if passwords don't match or are empty
    useEffect(() => {
        const arePasswordsValid = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;
        setIsButtonDisabled(!arePasswordsValid);
    }, [newPassword, confirmPassword]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Validate token
        if (!token) {
            setError("Invalid or missing reset token. Please request a new password reset.");
            return;
        }

        // Validate passwords
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters long.");
            return;
        }

        // Prevent multiple submissions
        if (isLoading) return;

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            console.log("Resetting password with token");

            // Call the password reset service
            await authService.resetPassword(token, newPassword);

            console.log("Password reset successful");
            setSuccess("Your password has been reset successfully. You can now log in with your new password.");

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 3000);

        } catch (err: any) {
            console.error("Password reset error:", err);
            let errorMessage = "An error occurred while processing your request.";

            if (err.response?.data) {
                // Handle structured error response
                if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                }
            } else if (err.response?.status === 400) {
                errorMessage = "Invalid or expired reset token. Please request a new password reset.";
            } else if (err.request) {
                errorMessage = "Server not responding. Please try again later.";
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-header">
                <img src="/assets/logos/Logo_Parallel.svg" alt="Parallel Logo" className="parallel-logo" />
            </div>
            <div className="login-box">
                <img src="/assets/logos/Logo_Operator_Inline.svg" alt="Parallel Operator" className="logo-operator-inline" />

                <form onSubmit={handleSubmit}>
                    <p className="reset-instructions">Please enter a new password below.</p>

                    <div className="input-group">
                        <input
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder=" "
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            name="newPassword"
                            disabled={isLoading}
                            required
                        />
                        <label htmlFor="newPassword">New Password</label>
                        {newPassword && (
                            <button type="button" onClick={toggleNewPasswordVisibility} className="password-toggle-button">
                                <img
                                    src={showNewPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                                    alt={showNewPassword ? "Hide password" : "Show password"}
                                    width="24"
                                    height="24"
                                />
                            </button>
                        )}
                    </div>

                    <div className="input-group">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder=" "
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            name="confirmPassword"
                            disabled={isLoading}
                            required
                        />
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        {confirmPassword && (
                            <button type="button" onClick={toggleConfirmPasswordVisibility} className="password-toggle-button">
                                <img
                                    src={showConfirmPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                                    alt={showConfirmPassword ? "Hide password" : "Show password"}
                                    width="24"
                                    height="24"
                                />
                            </button>
                        )}
                    </div>

                    <button
                        type="submit"
                        className={`login-button ${isLoading ? 'loading' : ''}${isButtonDisabled ? ' login-button-disabled' : ''}`}
                        disabled={isLoading || isButtonDisabled}
                    >
                        {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>

                    {error && <p className="error">{error}</p>}
                    {success && <p className="success">{success}</p>}
                </form>

                <button
                    className="back-to-login"
                    onClick={() => navigate('/login')}
                    disabled={isLoading}
                >
                    Login Instead
                </button>
            </div>
        </div>
    );
};

export default ResetPassword; 